'use client';

import { Cell, Pie, PieChart } from 'recharts';

import { useMemo } from 'react';

import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';

import { cn } from '@/lib/utils';

import { DEFAULT_PIE_COLORS } from './constants';
import { LockedOverlay } from './locked-overlay';
import { PieChartCardProps, PieDataPoint } from './types';

// Dummy data for locked/preview state
const DUMMY_PIE_DATA: PieDataPoint[] = [
	{ name: 'Category A', value: 35 },
	{ name: 'Category B', value: 25 },
	{ name: 'Category C', value: 20 },
	{ name: 'Category D', value: 15 },
	{ name: 'Category E', value: 5 },
];

/**
 * Reusable pie chart card component
 * Used for displaying distribution data (e.g., stage distribution)
 */
export function PieChartCard({
	title,
	subtitle,
	data,
	isLocked = false,
	isLoading = false,
	showLegend = true,
	valueFormatter,
	colors = DEFAULT_PIE_COLORS,
	height = 'h-48',
	className,
}: PieChartCardProps) {
	// Use dummy data when locked to avoid exposing real data in DOM
	const displayData = isLocked ? DUMMY_PIE_DATA : data;

	// Calculate total for percentage
	const total = useMemo(
		() => displayData.reduce((sum, item) => sum + item.value, 0),
		[displayData]
	);

	// Transform data with colors
	const chartData = useMemo(
		() =>
			displayData.map((point, index) => ({
				...point,
				color: point.color || colors[index % colors.length],
			})),
		[displayData, colors]
	);

	const chartConfig = useMemo(
		() =>
			chartData.reduce(
				(acc, item) => ({
					...acc,
					[item.name]: {
						label: item.name,
						color: item.color,
					},
				}),
				{}
			),
		[chartData]
	);

	if (isLoading) {
		return (
			<div className={cn('rounded-lg border border-border bg-card p-4', className)}>
				<Skeleton className="mb-2 h-5 w-32" />
				{subtitle && <Skeleton className="mb-4 h-4 w-24" />}
				<Skeleton className={cn('mx-auto aspect-square w-32 rounded-full', height)} />
				{showLegend && (
					<div className="mt-4 space-y-2">
						<Skeleton className="h-3 w-full" />
						<Skeleton className="h-3 w-3/4" />
						<Skeleton className="h-3 w-1/2" />
					</div>
				)}
			</div>
		);
	}

	return (
		<div className={cn('rounded-lg border border-border bg-card p-4', className)}>
			{/* Header */}
			<div className="mb-2">
				<h3 className="text-sm font-medium">{title}</h3>
				{subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
			</div>

			{/* Chart - Wrapped with locked overlay */}
			<LockedOverlay isLocked={isLocked}>
				{displayData?.length > 0 && total > 0 ? (
					<div>
						<ChartContainer
							config={chartConfig}
							className={cn('mx-auto [&]:aspect-auto', height)}
						>
							<PieChart>
								<ChartTooltip
									cursor={false}
									content={
										<ChartTooltipContent
											hideLabel
											formatter={(tooltipValue, name) => {
												const numValue = Number(tooltipValue);
												const percentage = ((numValue / total) * 100).toFixed(1);
												const formattedValue = valueFormatter
													? valueFormatter(numValue)
													: numValue.toString();
												return (
													<div className="flex items-center gap-2">
														<span>{name}</span>
														<span className="font-medium">
															{formattedValue} ({percentage}%)
														</span>
													</div>
												);
											}}
										/>
									}
								/>
								<Pie
									data={chartData}
									dataKey="value"
									nameKey="name"
									cx="50%"
									cy="50%"
									innerRadius="40%"
									outerRadius="80%"
									paddingAngle={2}
									strokeWidth={0}
								>
									{chartData.map((entry, index) => (
										<Cell
											key={`cell-${index}`}
											fill={entry.color}
										/>
									))}
								</Pie>
							</PieChart>
						</ChartContainer>

						{/* Legend */}
						{showLegend && (
							<div className="mt-3 space-y-1.5">
								{chartData.map((item) => {
									const percentage = ((item.value / total) * 100).toFixed(1);
									return (
										<div
											key={item.name}
											className="flex items-center justify-between text-xs"
										>
											<div className="flex items-center gap-2">
												<div
													className="h-2.5 w-2.5 rounded-full"
													style={{ backgroundColor: item.color }}
												/>
												<span className="text-muted-foreground">{item.name}</span>
											</div>
											<span className="font-medium">
												{valueFormatter ? valueFormatter(item.value) : item.value} ({percentage}%)
											</span>
										</div>
									);
								})}
							</div>
						)}
					</div>
				) : (
					<div
						className={cn(
							'flex w-full items-center justify-center text-sm text-muted-foreground',
							height
						)}
					>
						No data available
					</div>
				)}
			</LockedOverlay>
		</div>
	);
}
